import { zodResolver } from "@hookform/resolvers/zod";
import { Button, ButtonColor, PasswordInput } from "@swivl-mono/ui";
import { ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import AccountModel from "../../../../../Models/Account/AccountModel";
import { AlertsModel } from "../../../../../Models/Notifications/Alerts.Model";

const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, "Current password is required."),
    newPassword: z.string().min(8, "Password must be at least 8 characters long."),
    confirmPassword: z.string().min(1, "Please confirm your new password.")
  })
  .refine((data) => data.newPassword !== data.currentPassword, {
    message: "New password cannot be the same as your current password.",
    path: ["newPassword"]
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Confirm password must match your new password.",
    path: ["confirmPassword"]
  });

type ChangePasswordFormData = z.infer<typeof changePasswordSchema>;

export function SecurityTab(): ReactNode {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors, isSubmitting }
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema),
    mode: "onBlur",
    reValidateMode: "onBlur",
    defaultValues: {
      currentPassword: "",
      newPassword: "",
      confirmPassword: ""
    }
  });

  const [currentPasswordError, setCurrentPasswordError] = useState<string | undefined>();
  const [formError, setFormError] = useState<string | undefined>();

  const hasEmptyFields = !watch("currentPassword") || !watch("newPassword") || !watch("confirmPassword");
  const hasErrors = Object.keys(errors).length > 0 || !!currentPasswordError || !!formError;
  const isUpdateDisabled = hasEmptyFields || hasErrors;

  async function handleChangePassword(data: ChangePasswordFormData): Promise<void> {
    setCurrentPasswordError(undefined);
    setFormError(undefined);
    try {
      await AccountModel.updatePassword(data.currentPassword, data.newPassword);
      reset();
      AlertsModel.successToast("Password updated successfully");
    } catch (error) {
      const message = error instanceof Error ? error.message : "";
      if (message.includes("Invalid password")) {
        setCurrentPasswordError("The current password you entered does not match your existing password.");
      } else {
        setFormError("Failed to update password. Please try again.");
      }
    }
  }

  return (
    <div>
      <h2 className="heading-base-alt mb-4">Changing Password</h2>
      <form
        onSubmit={(e) => {
          setCurrentPasswordError(undefined);
          setFormError(undefined);
          handleSubmit(handleChangePassword)(e);
        }}
        className="flex flex-col gap-4"
      >
        <PasswordInput
          label="Current Password"
          placeholder="Input"
          {...register("currentPassword", { onChange: () => setCurrentPasswordError(undefined) })}
          error={currentPasswordError || errors.currentPassword?.message}
        />
        <PasswordInput label="New Password" placeholder="Input" {...register("newPassword")} error={errors.newPassword?.message} />
        <PasswordInput label="Confirm Password" placeholder="Input" {...register("confirmPassword")} error={errors.confirmPassword?.message} />
        {formError && <p className="text-sm text-red-500">{formError}</p>}
        <div>
          <Button type="submit" color={ButtonColor.ORANGE} disabled={isUpdateDisabled} loading={isSubmitting}>
            Update
          </Button>
        </div>
      </form>
    </div>
  );
}
